import { getAllSlots, migrateStateV3ToV4, isUnsupportedSchemaVersionError, SCHEMA_VERSION_LATEST, type ChordCakeStateV3, type SlotStateV3 } from '@chordbaker/music-theory';
import { NEWER_VERSION_MESSAGE, upgradeImportedSong } from './songSchema';
import { SectionState, SlotState, ChordCakeState } from '../types';
import {
  parseChordString,
  parseKeySignatureNullable,
  ChordObjectModelV3,
  ChordCakeStateV2,
  KeySignature,
  detectVersion,
  migrateStateV2ToV3,
  normalizeToV3,
  readSongMeta,
  type SongMeta,
} from '@chordbaker/music-theory';
import { isChordSheetEmpty } from './sheetUtils';
import { generateUniqueId } from './idUtils';
import { readSongFileText } from './chordbakerContainer';

export interface ImportOptions {
  onSuccess: (data: {
    title: string;
    sections: SectionState[];
    key?: KeySignature | null;
    /** Song tempo + global time signature */
    songMeta: SongMeta;
    guitarShapes?: Record<string, string>;
  }) => void;
  onError?: (error: string) => void;
  currentSongTitle: string;
  currentSections: SectionState[];
  onConfirmReplace?: () => void;
  onConfirmEmptyFile?: (proceedCallback: () => void) => void;
  onWarnNoChords?: (proceedCallback: () => void) => void;
  onConfirmReplaceExisting?: (proceedCallback: () => void) => void;
}

// ============================================================================
// V1 → V3 Migration
// ============================================================================

/**
 * Migrate V1 key string to KeySignature
 * Also handles hybrid files with V2 structure but no version field
 */
function migrateKeySignature(v1Key: string | KeySignature | null | undefined): KeySignature | null {
  if (!v1Key) return null;

  // If it's already a KeySignature object, return it as-is
  if (typeof v1Key === 'object' && v1Key.note && v1Key.mode) {
    return v1Key as KeySignature;
  }

  // Otherwise, it's a V1 string - parse it
  try {
    return parseKeySignatureNullable(v1Key as string);
  } catch (error) {
    console.warn(`Failed to migrate key signature "${v1Key}":`, error);
    return null;
  }
}

/**
 * Migrate a slot from any format to V3
 */
function migrateSlotToV3(slot: any): SlotStateV3 {
  let chordData: ChordObjectModelV3 | null = null;

  // Handle string chords (V1 format)
  if (typeof slot.chord === 'string' && slot.chord.trim() !== "" && slot.chord.trim() !== "- - -") {
    chordData = parseChordString(slot.chord); // Returns V3 format
  }
  // Handle object chords (V1 or V2 format)
  else if (typeof slot.chord === 'object' && slot.chord !== null && (slot.chord.fundamental || slot.chord.root)) {
    chordData = normalizeToV3(slot.chord); // Handles V1, V2, or V3
  }

  return {
    id: slot.id || generateUniqueId('slot'),
    chord: chordData,
    customText: slot.customText ?? null,
    lyrics: slot.lyrics ?? null,
  };
}

/**
 * Migrate V1 ChordCakeState directly to V3 format.
 * Uses parseChordString (returns V3) and normalizeToV3 for chord objects.
 */
function migrateChordCakeStateV1ToV3(v1State: any): ChordCakeStateV3 {
  return {
    version: 3,
    title: v1State.songTitle ?? '',
    key: migrateKeySignature(v1State.selectedKey) ?? migrateKeySignature(v1State.selectedKeyDisplay) ?? null,
    guitarShapes: {},
    sections: v1State.sections.map((section: any) => ({
      id: section.id || generateUniqueId('section'),
      title: section.title,
      rows: section.rows.map((row: any) => ({
        id: row.id || generateUniqueId('row'),
        slots: row.slots.map(migrateSlotToV3),
      })),
    })),
  };
}

// ============================================================================
// V2 Validation (for validating V2 files before migration)
// ============================================================================

/**
 * Validate V2 ChordCakeState structure
 */
function validateChordCakeStateV2(state: any): void {
  if (state.version !== 2) {
    throw new Error('Invalid version: expected 2');
  }

  if (typeof state.songTitle !== 'string') {
    throw new Error('Invalid songTitle: expected string');
  }

  if (!Array.isArray(state.sections)) {
    throw new Error('Invalid sections: expected array');
  }

  // Validate key signatures (if present)
  if (state.selectedKey !== null && state.selectedKey !== undefined) {
    validateKeySignature(state.selectedKey, 'selectedKey');
  }

  if (state.selectedKeyDisplay !== null && state.selectedKeyDisplay !== undefined) {
    validateKeySignature(state.selectedKeyDisplay, 'selectedKeyDisplay');
  }

  // Validate sections structure
  state.sections.forEach((section: any, sectionIndex: number) => {
    if (!section.id || typeof section.id !== 'string') {
      throw new Error(`Invalid section[${sectionIndex}].id: expected string`);
    }
    if (typeof section.title !== 'string') {
      throw new Error(`Invalid section[${sectionIndex}].title: expected string`);
    }
    if (!Array.isArray(section.rows)) {
      throw new Error(`Invalid section[${sectionIndex}].rows: expected array`);
    }

    section.rows.forEach((row: any, rowIndex: number) => {
      if (!row.id || typeof row.id !== 'string') {
        throw new Error(`Invalid section[${sectionIndex}].rows[${rowIndex}].id: expected string`);
      }
      if (!Array.isArray(row.slots)) {
        throw new Error(`Invalid section[${sectionIndex}].rows[${rowIndex}].slots: expected array`);
      }

      row.slots.forEach((slot: any, slotIndex: number) => {
        if (!slot.id || typeof slot.id !== 'string') {
          throw new Error(`Invalid section[${sectionIndex}].rows[${rowIndex}].slots[${slotIndex}].id: expected string`);
        }

        if (slot.chord !== null && slot.chord !== undefined) {
          validateChordV2(slot.chord, `section[${sectionIndex}].rows[${rowIndex}].slots[${slotIndex}].chord`);
        }
      });
    });
  });
}

function validateNote(note: any, fieldName: string): void {
  if (!note || typeof note !== 'object') {
    throw new Error(`Invalid ${fieldName}: expected object`);
  }

  if (!note.noteName || !note.modifier) {
    throw new Error(`Invalid ${fieldName}: missing noteName or modifier`);
  }

  const validNoteNames = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  if (!validNoteNames.includes(note.noteName)) {
    throw new Error(`Invalid ${fieldName}.noteName: "${note.noteName}" (must be C, D, E, F, G, A, or B)`);
  }

  const validModifiers = ['n', 's', 'f'];
  if (!validModifiers.includes(note.modifier)) {
    throw new Error(`Invalid ${fieldName}.modifier: "${note.modifier}" (must be n, s, or f)`);
  }
}

function validateChordV2(chord: any, fieldName: string): void {
  if (!chord || typeof chord !== 'object') {
    throw new Error(`Invalid ${fieldName}: expected object`);
  }

  if (!chord.fundamental) {
    throw new Error(`Invalid ${fieldName}: missing fundamental`);
  }
  validateNote(chord.fundamental, `${fieldName}.fundamental`);

  if (chord.bass !== null && chord.bass !== undefined) {
    validateNote(chord.bass, `${fieldName}.bass`);
  }
}

function validateKeySignature(key: any, fieldName: string): void {
  if (!key.note || !key.mode) {
    throw new Error(`Invalid ${fieldName}: missing note or mode`);
  }

  if (!key.note.noteName || !key.note.modifier) {
    throw new Error(`Invalid ${fieldName}.note: missing noteName or modifier`);
  }

  const validNoteNames = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  if (!validNoteNames.includes(key.note.noteName)) {
    throw new Error(`Invalid ${fieldName}.note.noteName: ${key.note.noteName}`);
  }

  const validModifiers = ['n', 's', 'f'];
  if (!validModifiers.includes(key.note.modifier)) {
    throw new Error(`Invalid ${fieldName}.note.modifier: ${key.note.modifier}`);
  }

  const validModes = ['maj', 'min'];
  if (!validModes.includes(key.mode)) {
    throw new Error(`Invalid ${fieldName}.mode: ${key.mode}`);
  }
}

// ============================================================================
// Main Import Functions
// ============================================================================

/**
 * Detect version and bring any input (V1/V2/V3/V4) to the app's V4 state.
 * - V1 → V3 directly (parseChordString and normalizeToV3 already return V3), then V4
 * - V2 → validate → V3 (via shared migrateStateV2ToV3), then V4
 * - V3 → validateAndNormalizeStateV3 → V4; V4 → validateAndNormalizeStateV4
 * - newer than this app → UnsupportedSchemaVersionError ("update the app")
 */
function migrateToV4(parsedData: any): ChordCakeState {
  const version = detectVersion(parsedData);

  if (version >= 3) {
    try {
      return upgradeImportedSong(parsedData);
    } catch (error) {
      // Made with a newer app than this one: say so instead of a shape error
      if (isUnsupportedSchemaVersionError(error)) throw new Error(NEWER_VERSION_MESSAGE);
      throw error;
    }
  }

  if (version === 2) {
    validateChordCakeStateV2(parsedData);
    return migrateStateV3ToV4(migrateStateV2ToV3(parsedData as ChordCakeStateV2));
  }

  // V1 → V3 directly, then V4
  return migrateStateV3ToV4(migrateChordCakeStateV1ToV3(parsedData));
}


export const importChordSheetFromFile = async (file: File, options: ImportOptions): Promise<void> => {
  const {
    onSuccess,
    onError,
    currentSongTitle,
    currentSections,
    onConfirmReplace,
    onConfirmEmptyFile,
    onWarnNoChords,
    onConfirmReplaceExisting
  } = options;

  // Validate file extension
  if (!file.name.endsWith('.chordbaker') && !file.name.endsWith('.json')) {
    const errorMsg = 'Invalid file type. Please select a .chordbaker or .json file.';
    onError?.(errorMsg);
    return;
  }

  const actuallyLoadFile = async (file: File) => {
    try {
      // Handles both the opaque container format and legacy plain-JSON files
      const fileContent = await readSongFileText(file);

      if (!fileContent) {
        const errorMsg = 'File content is empty or unreadable. Please try selecting the file again or check if the file was saved correctly.';
        onError?.(errorMsg);
        return;
      }

      // Parse JSON
      let parsedData: any;
      try {
        parsedData = JSON.parse(fileContent);
      } catch {
        const errorMsg = 'Invalid file format. The file must be a valid .chordbaker or .json file.';
        onError?.(errorMsg);
        return;
      }

      // Basic validation - check if it has the required ChordCake structure
      // V1/V2 use songTitle, V3 uses title
      const hasTitle = typeof parsedData.songTitle === 'string' || typeof parsedData.title === 'string';
      if (!hasTitle || !Array.isArray(parsedData.sections)) {
        const errorMsg = 'Invalid file format. The file must be a valid .chordbaker or .json file with title and sections.';
        onError?.(errorMsg);
        return;
      }

      // Detect version and migrate to V3
      let v3Data: ChordCakeState;

      try {
        v3Data = migrateToV4(parsedData);
      } catch (validationError) {
        const errorMsg = validationError instanceof Error
          ? `Invalid file format: ${validationError.message}`
          : 'Invalid file format';
        onError?.(errorMsg);
        return;
      }

      // Check if the chord sheet is effectively empty
      const isEmpty = isChordSheetEmpty(v3Data.title, v3Data.sections);
      if (isEmpty) {
        if (onConfirmEmptyFile) {
          onConfirmEmptyFile(() => actuallyProcessFile(v3Data));
          return;
        } else {
          const shouldContinue = confirm(
            'The selected file appears to be empty (no title or chords). Would you like to load it anyway?'
          );
          if (!shouldContinue) {
            return;
          }
        }
      }

      actuallyProcessFile(v3Data);
    } catch (error) {
      console.error('Error loading chord chart:', error);
      const errorMsg = `Error loading chord chart: ${error instanceof Error ? error.message : 'Unknown error'}`;
      onError?.(errorMsg);
    }
  };

  const actuallyProcessFile = (v3Data: ChordCakeState) => {
    try {
      // Check if any chords were actually found
      const hasAnyChords = getAllSlots(v3Data.sections).some(slot => slot.chord !== null);

      if (!hasAnyChords && !v3Data.title.trim()) {
        if (onWarnNoChords) {
          onWarnNoChords(() => {
            onSuccess({
              title: v3Data.title,
              sections: v3Data.sections,
              key: v3Data.key,
              songMeta: readSongMeta(v3Data),
              guitarShapes: v3Data.guitarShapes,
            });
          });
          return;
        } else {
          alert(
            'The chord chart appears to contain no chords or title. You may want to check if this is the correct file.'
          );
        }
      }

      // Call success callback with V3 data
      onSuccess({
        title: v3Data.title,
        sections: v3Data.sections,
        key: v3Data.key,
        songMeta: readSongMeta(v3Data),
        guitarShapes: v3Data.guitarShapes,
      });

    } catch (error) {
      console.error('Error processing chord chart:', error);
      const errorMsg = `Error processing chord chart: ${error instanceof Error ? error.message : 'Unknown error'}`;
      onError?.(errorMsg);
    }
  };

  // Check if current sheet is empty using shared utility
  if (!isChordSheetEmpty(currentSongTitle, currentSections)) {
    if (onConfirmReplaceExisting) {
      onConfirmReplaceExisting(async () => {
        onConfirmReplace?.();
        await actuallyLoadFile(file);
      });
      return;
    } else {
      const shouldReplace = confirm(
        'Importing a new chord chart will replace your current progress. Are you sure you want to continue?'
      );

      if (shouldReplace) {
        onConfirmReplace?.();
        await actuallyLoadFile(file);
      }
    }
  } else {
    await actuallyLoadFile(file);
  }
};

/**
 * Import chord sheet from Buffet by chart ID
 *
 * @param chartId - The UUID of the chart on Buffet
 * @returns Promise<ChordCakeState> - The chart data in V3 format
 * @throws Error if fetch fails or data is invalid
 */
export async function importChordSheetFromUrl(chartId: string): Promise<ChordCakeState> {
  const buffetApiUrl = import.meta.env.VITE_BUFFET_API_URL || 'https://buffet.chordbaker.com/api';
  const TIMEOUT_MS = 10000;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      // `schema=<latest>` says which schema this build reads; the backend serves
      // the oldest shape to clients that don't say so.
      const response = await fetch(`${buffetApiUrl}/charts/${chartId}?schema=${SCHEMA_VERSION_LATEST}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Chart not found on Buffet');
        } else if (response.status >= 500) {
          throw new Error('Buffet server error. Please try again later.');
        } else {
          throw new Error(`Failed to load chart: ${response.statusText}`);
        }
      }

      const responseData = await response.json();
      const chart = responseData.chart;

      return migrateToV4(chart);
    } catch (fetchError) {
      clearTimeout(timeoutId);

      // Check for AbortError by name (DOMException may not pass instanceof Error in all environments)
      if (fetchError && typeof fetchError === 'object' && 'name' in fetchError && (fetchError as any).name === 'AbortError') {
        throw new Error('Loading timed out. Please try again.');
      }

      throw fetchError;
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Network error. Please check your connection.');
    }
    throw error;
  }
}
